import { z } from 'zod';
/**
 * Control protocol sent over the LiveKit data channel (reliable, topic "control").
 * Single source of truth for both the customer app and the dashboard.
 *
 * Every message carries a `requestId` so responses (PHOTO_DONE / PHOTO_ERROR)
 * can be correlated with the command that triggered them.
 */
/** Camera-bearing device that produces photos. Phase 1 ships `handy` only; the
 * field exists so the tablet app (phase 3) can join the same protocol. */
export const deviceSourceSchema = z.enum(['handy', 'tablet']);
/** How a photo was captured on the device. */
export const photoSourceSchema = z.enum([
    /** Remotely triggered: ImageCapture.takePhoto() on capable devices. */
    'image_capture',
    /** Remotely triggered fallback: canvas still frame from the video track. */
    'canvas',
    /** Historical: native camera dialog. The remote flow was removed, but stored
     * photos keep this source value. */
    'native',
]);
const requestId = z.string().min(1).max(64);
/** Facing of a camera as reported by the browser, null when unknown (typical
 * for external USB/UVC cameras, which report no facing mode at all). */
export const facingModeSchema = z.enum(['user', 'environment']);
/** One selectable video input on the customer device. External USB/UVC
 * cameras appear here just like the built-in ones — they are addressable only
 * by `deviceId`, since they announce no facing mode. */
export const cameraDeviceSchema = z.object({
    /** MediaDeviceInfo.deviceId — stable per origin+device. */
    deviceId: z.string().min(1).max(256),
    /** enumerateDevices label, or a generated fallback ("Kamera 2"). */
    label: z.string().min(1).max(200),
    facingMode: facingModeSchema.nullable(),
});
export const takePhotoSchema = z.object({
    type: z.literal('TAKE_PHOTO'),
    requestId,
});
export const photoDoneSchema = z.object({
    type: z.literal('PHOTO_DONE'),
    requestId,
    photoId: z.string().min(1),
    source: photoSourceSchema,
    device: deviceSourceSchema,
    width: z.number().int().positive(),
    height: z.number().int().positive(),
});
export const photoErrorSchema = z.object({
    type: z.literal('PHOTO_ERROR'),
    requestId,
    reason: z.string().min(1).max(500),
});
export const setTorchSchema = z.object({
    type: z.literal('SET_TORCH'),
    requestId,
    on: z.boolean(),
});
export const setZoomSchema = z.object({
    type: z.literal('SET_ZOOM'),
    requestId,
    /** Absolute zoom value within the range announced via CAPABILITIES. */
    zoom: z.number().positive(),
});
export const switchCameraSchema = z.object({
    type: z.literal('SWITCH_CAMERA'),
    requestId,
    /** Exact camera from CAPABILITIES.cameras — takes precedence over `facing`
     * and is the only way to reach an external USB camera. */
    deviceId: z.string().min(1).max(256).optional(),
    /** Legacy/simple toggle: front vs. back. Ignored when `deviceId` is set;
     * omitting both flips to the other facing mode. */
    facing: facingModeSchema.optional(),
});
export const pointerSchema = z.object({
    type: z.literal('POINTER'),
    requestId,
    /** Normalized coordinates relative to the video frame, 0..1. */
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
});
export const hintSchema = z.object({
    type: z.literal('HINT'),
    requestId,
    text: z.string().min(1).max(200),
});
/** Announced by the customer device right after joining the room. The dashboard
 * hides every remote control the device does not support (no dead buttons). */
export const capabilitiesSchema = z.object({
    type: z.literal('CAPABILITIES'),
    requestId,
    device: deviceSourceSchema,
    platform: z.enum(['android', 'ios', 'desktop', 'other']),
    /** True when the torch can be toggled via applyConstraints. */
    torch: z.boolean(),
    /** Zoom range, or null when the device cannot zoom via constraints. */
    zoom: z
        .object({
        min: z.number(),
        max: z.number(),
        step: z.number().optional(),
    })
        .nullable(),
    /** True when ImageCapture.takePhoto() is available (full-resolution remote photos). */
    imageCapture: z.boolean(),
    facingModes: z.array(facingModeSchema),
    /** Every video input the device offers, including external USB/UVC cameras.
     * Defaults to an empty list so clients predating the camera picker still
     * validate. */
    cameras: z.array(cameraDeviceSchema).max(32).default([]),
    /** deviceId of the currently published camera, null when unknown. */
    activeCameraId: z.string().min(1).max(256).nullable().default(null),
});
export const controlMessageSchema = z.discriminatedUnion('type', [
    takePhotoSchema,
    photoDoneSchema,
    photoErrorSchema,
    setTorchSchema,
    setZoomSchema,
    switchCameraSchema,
    pointerSchema,
    hintSchema,
    capabilitiesSchema,
]);
/** LiveKit data channel topic used for all control messages. */
export const CONTROL_TOPIC = 'control';
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();
/** Serialize a control message for the LiveKit data channel. Validates first. */
export function encodeControlMessage(message) {
    const parsed = controlMessageSchema.parse(message);
    return textEncoder.encode(JSON.stringify(parsed));
}
/** Parse bytes from the data channel. Returns null for malformed payloads
 * instead of throwing — a misbehaving peer must never crash the UI. */
export function decodeControlMessage(payload) {
    let raw;
    try {
        raw = JSON.parse(textDecoder.decode(payload));
    }
    catch {
        return null;
    }
    const result = controlMessageSchema.safeParse(raw);
    return result.success ? result.data : null;
}
//# sourceMappingURL=commands.js.map